import { acceptHMRUpdate, defineStore } from 'pinia'
import { ErrorCodes, MetaErrorCodes, type BaseError, type Meta } from '@cplug/frontend-core/models'
import { Result } from '@cplug/frontend-core/utils'
import { BaseCRUDStore } from '@cplug/frontend-core/stores'
import type { CRUDGateway } from '@cplug/frontend-core/gateways'
import type { DashboardUser } from '../models/DashboardUser'
import { CPUserGateway } from '../gateways/CPUserGateway'
import { PermissionGroupId } from '../../permission-groups/models/UserPermissionsGroup'
import { useUserGateway, useUserModuleFeatures } from '../../composables/useUserModuleConfig'
import { useIsValidEmail } from '~/shared/composables/useEmail'

class UserStore extends BaseCRUDStore<DashboardUser> {
  protected get gateway (): CRUDGateway<DashboardUser> {
    return useUserGateway()
  }

  protected findErrors (model: DashboardUser): BaseError[] {
    const defaultErrors = (meta: Meta): BaseError => ({ code: ErrorCodes.DataValidation, meta })
    const errors: BaseError[] = []
    const features = useUserModuleFeatures()

    if ((model.name?.trim().length ?? 0) <= 0) {
      errors.push(defaultErrors({ name: MetaErrorCodes.Empty }))
    }

    if ((model.name?.trim().length ?? 0) > features.userNameMaxLength) {
      errors.push(defaultErrors({ name: MetaErrorCodes.LimitExceeded }))
    }

    if (
      features.userNameMinLength > 1 &&
      (model.name?.trim().length ?? 0) > 0 &&
      (model.name?.trim().length ?? 0) < features.userNameMinLength
    ) {
      errors.push(defaultErrors({ name: MetaErrorCodes.Minimum }))
    }

    if (model.permissionGroup?.id === PermissionGroupId.SalesPerson && !features.skipSalesPersonValidation) {
      return errors
    }

    const email = model.emails?.[0]?.trim() ?? ''
    const shouldValidateEmail = !features.validateUserEmailOnCreateOnly || model.id == null

    if (shouldValidateEmail) {
      if (email.length <= 0) {
        errors.push(defaultErrors({ email: MetaErrorCodes.Empty }))
      }

      if (email.length > 0 && !useIsValidEmail(email)) {
        errors.push(defaultErrors({ email: MetaErrorCodes.WrongFormat }))
      }

      if (email.length > 120) {
        errors.push(defaultErrors({ email: MetaErrorCodes.LimitExceeded }))
      }
    }

    if (features.requireUserPasswordOnCreate && model.id == null && (model.password?.length ?? 0) === 0) {
      errors.push(defaultErrors({ password: MetaErrorCodes.Empty }))
    }

    if ((model.password?.length ?? 0) > 0 && (model.password?.length ?? 0) < 8) {
      errors.push(defaultErrors({ password: MetaErrorCodes.Minimum }))
    }

    if (
      (model.password?.length ?? 0) > 0 &&
      (model as DashboardUser & { passwordConfirmation?: string }).passwordConfirmation != null &&
      model.password !== (model as DashboardUser & { passwordConfirmation?: string }).passwordConfirmation
    ) {
      errors.push(defaultErrors({ confirmPassword: MetaErrorCodes.WrongValue }))
    }

    if (model.permissionGroup == null) {
      errors.push(defaultErrors({ permissionGroup: MetaErrorCodes.Empty }))
    }

    if ((model.companies?.length ?? 0) < 1) {
      errors.push(defaultErrors({ companies: MetaErrorCodes.Empty }))
    }

    return errors
  }
}

const useUserStore = defineStore('users', () => {
  const userStore = new UserStore()

  const restore = async (userId: number): Promise<Result<void>> => {
    if (!useUserModuleFeatures().canRestoreUsers) {
      return Result.ofError({ code: ErrorCodes.NotFound })
    }

    return await CPUserGateway.instance().restore(userId) as Result<void>
  }

  return { ...userStore.defaultStore(), restore }
})

export default useUserStore

/* c8 ignore next 3 */
if ((import.meta as any).hot) {
  (import.meta as any).hot.accept(acceptHMRUpdate(useUserStore, (import.meta as any).hot))
}
